import React, { FunctionComponent, useState, useEffect } from 'react';

import { isUndefined } from 'lodash';

import {
    DropdownPicker,
    Props as DropdownPickerProps,
    DropdownData,
    DropdownDataWithHeader,
} from 'common/dropdown-picker/DropdownPicker';

interface OnSearchTextChangeFunc {
    (searchString: string): Promise<DropdownData[] | DropdownDataWithHeader[]>;
}

interface OnOpenFunc {
    (): Promise<DropdownData[] | DropdownDataWithHeader[]>;
}

interface Handlers {
    onSelect?: any;
    onClose?: any;
    onDataLoad?: any;
    onSearchTextChange?: OnSearchTextChangeFunc;
    onOpen?: OnOpenFunc;
    onClick?: any;
    onFetchMore?: any;
}

export interface Props extends Omit<DropdownPickerProps, 'dropdownData' | 'handlers'> {
    dropdownRef?: any;
    mentionsData?: DropdownData[];
    handlers?: Handlers;
}

export const MentionPicker: FunctionComponent<Props> = ({
    dropdownRef,
    mentionsData,
    handlers,
    searchOptions,
    displayOptions,
    enableAutoSelect,
    autoFocus,
    overlayArea,
}): JSX.Element => {
    const [hasDataLoaded, setHasDataLoaded] = useState(false);
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState<string>();

    useEffect(() => {
        let timer: any = null;
        const handleDataLoad = (data: any): void => {
            timer = setTimeout(() => {
                setHasDataLoaded(true);
                setData(data);
                if (!autoFocus && handlers && handlers.onDataLoad) {
                    handlers.onDataLoad();
                }
            }, 0);
        };
        const getMentionsData = async (): Promise<void> => {
            if (handlers && handlers?.onOpen) {
                const newData = await handlers?.onOpen();

                handleDataLoad(newData);
            } else {
                handleDataLoad(mentionsData);
            }
        };

        getMentionsData();
        return (): void => clearTimeout(timer);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const getData = async (): Promise<void> => {
            if (handlers!.onSearchTextChange) {
                setData([]);
                setHasDataLoaded(false);
                const newData = await handlers!.onSearchTextChange(searchOptions?.defaultSearchText!);

                setData(newData as any);
                setHasDataLoaded(true);
            }
        };

        if (!isUndefined(searchText) && searchOptions && searchOptions?.defaultSearchText !== searchText) {
            getData();
            if (!autoFocus && handlers && handlers.onDataLoad) {
                handlers.onDataLoad();
            }
        }
        setSearchText(searchOptions?.defaultSearchText!);
    }, [searchOptions?.defaultSearchText]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelection = (value: DropdownData): void => {
        if (handlers!.onSelect) {
            handlers!.onSelect(value);
        }
    };

    const handleClose = (): void => {
        if (handlers!.onClose) {
            handlers!.onClose();
        }
    };

    const handleSearchTextChange = async (searchString: string): Promise<void> => {
        setSearchText(searchString);
        if (handlers!.onSearchTextChange) {
            setData([]);
            setHasDataLoaded(false);
            const newData = await handlers!.onSearchTextChange(searchString);

            setData(newData as any);
            setHasDataLoaded(true);
        }
    };

    return (
        <div className="r4e-mention-picker">
            <DropdownPicker
                ref={dropdownRef}
                searchOptions={{
                    ...searchOptions,
                    defaultSearchText: searchText,
                }}
                displayOptions={displayOptions}
                dropdownData={data!}
                hasDataLoaded={hasDataLoaded}
                enableAutoSelect={enableAutoSelect}
                hasOverlayPromise
                autoFocus={autoFocus}
                overlayArea={overlayArea}
                handlers={{
                    onSelect: handleSelection,
                    onOutsideClick: handleClose,
                    onSearchTextChange: handleSearchTextChange,
                    onFetchMore: handlers!.onFetchMore,
                }}
            />
        </div>
    );
};

MentionPicker.defaultProps = {
    mentionsData: [],
};
